import { useCallback, useMemo } from "react";
import { Linking } from "react-native";
import { useRouter } from "expo-router";
import * as Crypto from "expo-crypto";

import { useAuthStore } from "@src/features/auth/auth-store";
import { useSessionStore } from "@src/stores/session-store";
import { authenticatedFetch } from "@src/features/auth/services/authenticated-fetch";
import { getEnvironment } from "@src/config/environment-store";
import { directory } from "@src/config/runtime";
import { showErrorToast, showInfoToast, showSuccessToast } from "@src/utils/toast";
import { appStorage, AppStorageKeys } from "@src/services/app-storage";
import {
  createDeviceTokenApi,
  usePushNotifications as usePushNotificationsBase,
  usePushNotificationSwitch as usePushNotificationSwitchBase,
  useHasActivePushDevice as useHasActivePushDeviceBase,
  teardownDeviceToken,
} from "@visioni/a3s-notifications/app";
import type {
  DeviceTokenApi,
  PushNotificationSwitchState,
  StoredDeviceToken,
  UsePushNotificationSwitchOptions,
} from "@visioni/a3s-notifications/app";

async function directoryBaseUrl(): Promise<string> {
  const env = await getEnvironment();
  return directory(env);
}

let sharedApiClient: DeviceTokenApi | undefined;

function getOrCreatePushDeviceId(): string {
  const existing = appStorage.get<string>(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_ID);
  if (existing) return existing;
  const next = Crypto.randomUUID();
  appStorage.set(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_ID, next);
  return next;
}

function getDeviceTokenApi(): DeviceTokenApi {
  sharedApiClient ??= createDeviceTokenApi({
    authenticatedFetch,
    directoryBaseUrl,
    deviceId: getOrCreatePushDeviceId,
  });
  return sharedApiClient;
}

export function usePushNotifications(options?: { enabled?: boolean }) {
  const router = useRouter();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const isUnlocked = useSessionStore((s) => s.isUnlocked);

  const deviceTokenApi = useMemo(() => getDeviceTokenApi(), []);
  const showForegroundToast = useCallback(
    (title: string, body?: string) => showInfoToast({ message: title, description: body }),
    [],
  );
  const shouldAutoRegisterToken = useCallback(
    (_token: string) => appStorage.has(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN),
    [],
  );

  usePushNotificationsBase({
    auth: { isAuthenticated, isInitialized, isUnlocked },
    router,
    showForegroundToast,
    deviceTokenApi,
    shouldAutoRegisterToken,
    enabled: options?.enabled ?? true,
  });
}

/** The same slot `shouldAutoRegisterToken` reads — see `usePushNotifications`. */
const storedDeviceToken: StoredDeviceToken = {
  get: () => appStorage.get<string>(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN),
  set: (token) => appStorage.set(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN, token),
  remove: () => appStorage.remove(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN),
};

/** This device's push registration switch, wired to the app's token API, storage and toasts. */
export function usePushNotificationSwitch(
  options: Partial<Pick<UsePushNotificationSwitchOptions, "feedback">> & { enabled: boolean },
): PushNotificationSwitchState {
  return usePushNotificationSwitchBase({
    deviceTokenApi: getDeviceTokenApi(),
    storedToken: storedDeviceToken,
    showSuccessToast: (message) => showSuccessToast({ message }),
    showErrorToast: (message) => showErrorToast({ message }),
    openSystemSettings: () => void Linking.openSettings(),
    enabled: options.enabled,
    ...(options.feedback !== undefined && { feedback: options.feedback }),
  });
}

/** Whether any of the user's devices is registered for push — for web, which has no device token of its own. */
export function useHasActivePushDevice(options: { enabled: boolean }) {
  return useHasActivePushDeviceBase({ deviceTokenApi: getDeviceTokenApi(), enabled: options.enabled });
}

export async function unregisterPushToken(): Promise<void> {
  appStorage.remove(AppStorageKeys.PUSH_NOTIFICATIONS_DEVICE_TOKEN);
  return teardownDeviceToken(getDeviceTokenApi(), { lookupToken: true });
}

export { getDeviceTokenApi };
